/**
 * In-memory mock API for local UI testing without a backend.
 * Activated by VITE_USE_MOCK_API=true. Data resets on page refresh.
 *
 * Mirrors the shape of `createApi()` in lib/api.ts so it can be swapped in
 * transparently. Not intended for production builds - guard at the call site.
 */

import type { Api } from './api'
import { deadlineAt, rulesProblem, windowProblem } from './boardDates'
import { createMockSurveyApi } from './mockSurveys'
import { roleGroupsProblem } from './roleGroups'
import { monthLabel, shiftMonth } from './report'
import { defaultWindow } from './reportingWindow'
import { APP_ROLES } from '../types'
import type {
  AppRole,
  AuditEntityType,
  AuditLogEntry,
  AuditLogPage,
  AuditLogQuery,
  BoardDateRules,
  BoardDateRulesUpdate,
  DatePrecision,
  DdiDraftEvent,
  DdiDraftResult,
  DirectoryUser,
  MeResponse,
  Project,
  SurveyResponse,
  Settings,
  Product,
  ReportingWindowUpdate,
  GroupGrant,
  RoleGroupMap,
  RoleGroupMapping,
  UserRef,
  AuditEntry,
  FieldMapping,
  MonthReview,
  ProjectReport,
  RagStatus,
  WindowSaveResult,
} from '../types'

const SELF: UserRef = {
  oid: 'mock-self-oid',
  name: 'Local Tester',
  email: 'local.tester@danone.com',
}

// The people who own the seeded projects, as they appear in the source records.
const IAN: UserRef = { oid: 'ian-oid', name: 'Ian Jackson', email: 'ian.jackson@danone.com' }
const NAYAN: UserRef = { oid: 'nayan-oid', name: 'Nayan Patel', email: 'nayan.patel@danone.com' }
const SWAPNA: UserRef = { oid: 'swapna-oid', name: 'Swapna Pawan', email: 'swapna.pawan@danone.com' }
const MIGUEL: UserRef = { oid: 'miguel-oid', name: 'Miguel De Frutos', email: 'miguel.defrutos@danone.com' }

const nowMinus = (days: number) => new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
const isoDate = (days: number) => nowMinus(days).slice(0, 10)
const newId = (prefix: string) =>
  `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

// ── Seed data ────────────────────────────────────────────────────────────────

const seedSettings: Settings = {
  subPortfolios: ['Ops Fin', 'WISE²', 'Marketing', 'Liverpool', 'RGM', 'Nutricia', 'Supply Planning'],
  // Sample groupings so the DDI slide's portfolio rail has something real to
  // group by. Every sub-portfolio above belongs to exactly one.
  portfolios: [
    { name: 'Commercial', subPortfolios: ['Marketing', 'RGM'] },
    { name: 'Finance', subPortfolios: ['Ops Fin'] },
    { name: 'SN', subPortfolios: ['Nutricia'] },
    { name: 'Supply Chain', subPortfolios: ['Liverpool', 'Supply Planning'] },
    { name: 'HR', subPortfolios: ['WISE²'] },
  ],
  onTimeDays: 14,
  atCostPct: 10,
  riskForecastMinScore: 4,
  teams: ['Operations', 'Support Functions', 'Commercial'],
  valueRealisedTargetHrs: 100000,
  valueRealisedTargetStartYear: 2025,
  valueRealisedTargetEndYear: 2027,
  powerAutomateUrl: 'https://prod-00.westeurope.logic.azure.com/workflows/mock',
  reminderGroupEmail: 'it-data-uki@danone.com',
  requestAccessUrl: 'https://danone.service-now.com/esc?id=sc_cat_item&sys_id=trackit-access-request',
}

const seedProducts: Product[] = [
  {
    id: 'prod_visibly',
    name: 'Visibly',
    vendor: 'Visibly',
    description: 'Employee compliance and capability platform.',
    ownerRef: NAYAN,
    contributors: [],
    createdBy: NAYAN,
    createdAt: nowMinus(200),
  },
  {
    id: 'prod_guardian',
    name: 'Guardian',
    vendor: 'SAP',
    description: 'Quality management system used across the Operations sites.',
    ownerRef: MIGUEL,
    contributors: [],
    createdBy: MIGUEL,
    createdAt: nowMinus(300),
  },
  {
    id: 'prod_digital_marketing',
    name: 'Digital Marketing - Websites & Content, Mobile Apps mgmt',
    vendor: 'Adobe',
    description: 'AEM corporate websites, content and mobile app management.',
    ownerRef: SWAPNA,
    contributors: [],
    createdBy: SWAPNA,
    createdAt: nowMinus(250),
  },
]

// Self-seeded user directory. In real life this is populated by the backend
// every time someone signs in. For mock mode we pre-fill it with the same
// people who appear as project/product owners so the picker has someone to
// click on.
//
// One of each role: the signed-in tester is a Platform admin, Nayan is a
// The Entra groups TrackIT knows about, by object ID: membership lives in
// Entra, and the mapping below says what each one grants.
const GROUPS = {
  platformAdmins: '710172aa-d099-4521-88ea-4d803037fe1c',
  reportingAdmins: 'ea5b5ca5-af29-41c6-b3e2-025ea86d0935',
  dangoAdmins: '8d6c2100-e129-49df-8ea5-0f3e1218dca2',
  // Placeholders: the survey groups are still to be created in Entra.
  surveyAdmins: '5a1e0c2b-0000-4000-8000-00000000a001',
  surveyResults: '5a1e0c2b-0000-4000-8000-00000000a002',
}

const seedRoleGroups: RoleGroupMapping[] = [
  { groupId: GROUPS.reportingAdmins, groupName: 'AAD-GAA-WW-TrackIT-Reporting-Admins', role: 'StatusReportingAdmin', scopes: [] },
  { groupId: GROUPS.surveyAdmins, groupName: 'AAD-GAA-WW-TrackIT-Survey-Admins', role: 'SurveyAdmin', scopes: [] },
  { groupId: GROUPS.surveyResults, groupName: 'AAD-GAA-WW-TrackIT-Survey-Results', role: 'SurveyResults', scopes: [] },
]

// Who holds what, and through which group. Anyone in no mapped group is a
// plain User, which is everyone who signs in.
const seedUser = (
  ref: UserRef, firstSeen: number, lastSeen: number, fromGroups: GroupGrant[] = [],
): DirectoryUser => ({
  id: ref.oid,
  oid: ref.oid,
  name: ref.name,
  email: ref.email,
  firstSeenAt: nowMinus(firstSeen),
  lastSeenAt: nowMinus(lastSeen),
  ...(fromGroups.length ? { groupGrants: fromGroups, groupRolesAt: nowMinus(lastSeen) } : {}),
})

const viaGroup = (role: AppRole, groupId: string): GroupGrant => ({ role, scopes: [], groupId })

const seedUsers: DirectoryUser[] = [
  seedUser(SELF, 30, 0, [viaGroup('PlatformAdmin', GROUPS.platformAdmins)]),
  seedUser(IAN, 220, 2, [viaGroup('PlatformAdmin', GROUPS.platformAdmins)]),
  seedUser(NAYAN, 240, 1, [viaGroup('StatusReportingAdmin', GROUPS.reportingAdmins), viaGroup('SurveyAdmin', GROUPS.surveyAdmins)]),
  seedUser(SWAPNA, 200, 4, [viaGroup('StatusReportingAdmin', GROUPS.reportingAdmins)]),
  seedUser(MIGUEL, 320, 9, [viaGroup('SurveyResults', GROUPS.surveyResults)]),
]

const auditEntry = (field: string, before: unknown, after: unknown, daysAgo: number, actor: UserRef): AuditEntry => ({
  at: nowMinus(daysAgo),
  actor,
  field,
  before,
  after,
})

// Build a seed ledger from a go-live date: expectedValue/12 spread from go-live
// month through Dec, plus full Jan-Dec for any forecast years up to current year.
function buildSeedLedgerFromDate(expectedValue: number, goLiveDate: Date) {
  const rate = expectedValue / 12
  const curYear = new Date().getFullYear()
  const ledger: Record<string, { base: number; adjustments: number; adjustmentNotes: string[] }> = {}
  // Go-live year (prorated)
  for (let m = goLiveDate.getMonth() + 1; m <= 12; m++) {
    ledger[`${goLiveDate.getFullYear()}-${String(m).padStart(2, '0')}`] = { base: rate, adjustments: 0, adjustmentNotes: [] }
  }
  // Forecast years
  for (let y = goLiveDate.getFullYear() + 1; y <= curYear; y++) {
    for (let m = 1; m <= 12; m++) {
      ledger[`${y}-${String(m).padStart(2, '0')}`] = { base: rate, adjustments: 0, adjustmentNotes: [] }
    }
  }
  return ledger
}

// Four real UK&I records, transcribed from the project pages. Three fields the
// source records do not carry are marked ASSUMED and are safe to correct:
// the delivery stage, the monthly RAG, and the DDI summary/next-steps wording.
const seedProjects: Project[] = [
  {
    id: 'proj_einvoice_fi',
    reportable: true,
    ddi: false,
    stage: 'scale',                       // ASSUMED: live since July, record still Forecast
    name: 'E-invoice Finland',
    description:
      'FI requirement about 3 e-invoices format exchange if Customer request any of those 3 formats\n' +
      '- Legal background: it is not e-invoicing with FI government yet, but still it is legal requirement since few years already but there was no pushed by FI Customer in the past (now they start to ask Danone)\n' +
      '- timing 01-07.2026\n' +
      '- scope: EDI sales invoicing scope only\n' +
      '- solution: local solution via Comarch (existing NORBAL edi provider) chosen as Opentext could not cover all requirements (approved by IT Global Sales and Architect)\n' +
      '- what if not project done (2026) - if Customer comes and says i want in Invoice in peppol format and Danone not ready => Customer can stop payments (no legal penalties from government though)',
    subPortfolio: 'Ops Fin',
    team: 'Operations',
    tier: 'Black Line',
    status: 'forecast',
    dueDate: '2026-07-31',
    forecastCost: 79950,
    // No benefit case on the source record. Zero rather than an invented figure.
    expectedValue: 0,
    actualCost: 72400,
    stakeholders: [],
    surveyToken: 'tok_einvoice_fi',
    createdBy: IAN,
    auditTrail: [],
  },
  {
    id: 'proj_visibly_hq',
    reportable: true,
    ddi: false,
    stage: 'realise',                     // ASSUMED: complete and live since April
    name: 'Visibly HQ',
    description:
      'VisiblyHQ Phase 1 automated key employee compliance processes, delivering significant time savings and stronger compliance controls',
    subPortfolio: 'WISE²',
    team: 'Support Functions',
    tier: 'Winning',
    status: 'complete',
    dueDate: '2026-04-22',
    actualGoLive: '2026-04-21',
    deliveredInFull: 'Y',
    forecastCost: 30000,
    actualCost: 0,
    adoptionMode: 'percent',
    expectedUsers: 4,
    expectedValue: 3120,
    stakeholders: [],
    surveyToken: 'tok_visibly_hq',
    productId: 'prod_visibly',
    createdBy: NAYAN,
    auditTrail: [auditEntry('status', 'forecast', 'complete', 114, NAYAN)],
    lastMeaningfulUpdate: nowMinus(114),
    monthlyLedger: buildSeedLedgerFromDate(3120, new Date('2026-04-21')),
  },
  {
    id: 'proj_norbal_websites',
    reportable: true,
    ddi: false,
    stage: 'develop',                     // ASSUMED: go live December, migration under way
    name: 'NORBAL Websites Harmonization - 7 corporate websites',
    description: 'Migration of all 7 NORBAL corporate websites to AEM backend (from Wordpress)',
    subPortfolio: 'Marketing',
    team: 'Commercial',
    tier: 'Black Line',
    status: 'forecast',
    dueDate: '2026-12-31',
    deliveredInFull: 'N',
    forecastCost: 0,
    actualCost: 0,
    adoptionMode: 'users',
    // Transcribed as-is from the source record, which holds a negative count.
    expectedUsers: -2,
    // Left blank on the source record. Zero rather than an invented figure.
    expectedValue: 0,
    stakeholders: [],
    surveyToken: 'tok_norbal_websites',
    productId: 'prod_digital_marketing',
    createdBy: SWAPNA,
    auditTrail: [],
  },
  {
    id: 'proj_guardian_liverpool',
    reportable: true,
    ddi: false,
    stage: 'realise',                     // ASSUMED: complete and live since December 2025
    name: 'Guardian Raws & Packs Project - Liverpool',
    description: "Implementation of Danone's Guardian QMS Raws & Pack (RP) module for Liverpool.",
    subPortfolio: 'Liverpool',
    team: 'Operations',
    tier: 'License to Operate',
    status: 'complete',
    dueDate: '2025-12-01',
    actualGoLive: '2025-12-01',
    deliveredInFull: 'Y',
    forecastCost: 52850,
    actualCost: 46000,
    adoptionMode: 'na',
    expectedValue: 728,
    stakeholders: [],
    surveyToken: 'tok_guardian_liverpool',
    productId: 'prod_guardian',
    createdBy: MIGUEL,
    auditTrail: [auditEntry('status', 'forecast', 'complete', 255, MIGUEL)],
    lastMeaningfulUpdate: nowMinus(255),
    monthlyLedger: buildSeedLedgerFromDate(728, new Date('2025-12-01')),
  },
  {
    id: 'proj_homeward_o2c',
    reportable: true,
    ddi: true,
    stage: 'develop',
    name: 'Homeward O2C',
    subPortfolio: 'Marketing',
    team: 'Operations',
    tier: 'Black Line',
    status: 'forecast',
    dueDate: '2026-09-30',
    forecastCost: 1800000,
    expectedValue: 900,
    stakeholders: [],
    surveyToken: 'tok_homeward_o2c',
    createdBy: IAN,
    auditTrail: [],
  },
  {
    id: 'proj_docxp',
    reportable: true,
    ddi: true,
    stage: 'discover',
    name: 'DocXP Replacement',
    subPortfolio: 'Marketing',
    team: 'Operations',
    tier: 'Winning',
    status: 'forecast',
    dueDate: '2027-01-31',
    forecastCost: 500000,
    expectedValue: 0,
    stakeholders: [],
    surveyToken: 'tok_docxp',
    createdBy: IAN,
    auditTrail: [],
  },
  {
    id: 'proj_baby_bounceback',
    reportable: true,
    ddi: true,
    stage: 'discover',
    name: 'Baby Bounceback',
    subPortfolio: 'Nutricia',
    team: 'Commercial',
    tier: 'Winning',
    status: 'forecast',
    dueDate: '2026-12-31',
    forecastCost: 0,
    expectedValue: 0,
    stakeholders: [],
    surveyToken: 'tok_baby_bounceback',
    createdBy: SWAPNA,
    auditTrail: [],
  },
  {
    id: 'proj_sampling_service',
    reportable: true,
    ddi: true,
    stage: 'scale',
    name: 'Sampling Service',
    subPortfolio: 'Nutricia',
    team: 'Commercial',
    tier: 'Black Line',
    status: 'forecast',
    dueDate: '2026-07-21',
    forecastCost: 1100000,
    expectedValue: 0,
    stakeholders: [],
    surveyToken: 'tok_sampling_service',
    createdBy: SWAPNA,
    auditTrail: [],
  },
  {
    id: 'proj_voice_ai_homecare',
    reportable: true,
    ddi: true,
    stage: 'develop',
    name: 'Voice AI for Homecare',
    subPortfolio: 'Nutricia',
    team: 'Commercial',
    tier: 'Winning',
    status: 'forecast',
    dueDate: '2026-05-31',
    forecastCost: 100000,
    expectedValue: 0,
    stakeholders: [],
    surveyToken: 'tok_voice_ai_homecare',
    createdBy: SWAPNA,
    auditTrail: [],
  },
  {
    id: 'proj_gross_to_net',
    reportable: true,
    ddi: true,
    stage: 'design',
    name: 'Gross-to-Net UKI',
    subPortfolio: 'RGM',
    team: 'Operations',
    tier: 'Black Line',
    status: 'forecast',
    dueDate: '2027-01-31',
    forecastCost: 500000,
    expectedValue: 1_200,
    stakeholders: [],
    surveyToken: 'tok_gross_to_net',
    createdBy: IAN,
    auditTrail: [],
  },
  {
    id: 'proj_ne_one_pl',
    reportable: true,
    ddi: true,
    stage: 'scale',
    name: 'NE One P&L',
    subPortfolio: 'Ops Fin',
    team: 'Operations',
    tier: 'Black Line',
    status: 'forecast',
    dueDate: '2026-09-15',
    forecastCost: 100000,
    expectedValue: 0,
    stakeholders: [],
    surveyToken: 'tok_ne_one_pl',
    createdBy: IAN,
    auditTrail: [],
  },
  {
    id: 'proj_finance_backbone',
    reportable: true,
    ddi: true,
    stage: 'develop',
    name: 'Finance Backbone',
    subPortfolio: 'Ops Fin',
    team: 'Operations',
    tier: 'Black Line',
    status: 'forecast',
    dueDate: '2026-12-31',
    forecastCost: 400000,
    expectedValue: 0,
    stakeholders: [],
    surveyToken: 'tok_finance_backbone',
    createdBy: IAN,
    auditTrail: [],
  },
  {
    id: 'proj_uki_ccd',
    reportable: true,
    ddi: true,
    stage: 'design',
    name: 'UKI CCD Automation',
    subPortfolio: 'Ops Fin',
    team: 'Operations',
    tier: 'Black Line',
    status: 'forecast',
    dueDate: '',
    forecastCost: 0,
    expectedValue: 0,
    stakeholders: [],
    surveyToken: 'tok_uki_ccd',
    createdBy: NAYAN,
    auditTrail: [],
  },
  {
    id: 'proj_drs',
    reportable: true,
    ddi: true,
    stage: 'discover',
    name: 'DRS',
    subPortfolio: 'Supply Planning',
    team: 'Operations',
    tier: 'License to Operate',
    status: 'forecast',
    dueDate: '2027-10-31',
    forecastCost: 450000,
    expectedValue: 0,
    stakeholders: [],
    surveyToken: 'tok_drs',
    createdBy: IAN,
    auditTrail: [],
  },
  {
    id: 'proj_ie_duty',
    reportable: true,
    ddi: true,
    stage: 'develop',
    name: 'IE Duty Mgt',
    subPortfolio: 'Supply Planning',
    team: 'Operations',
    tier: 'License to Operate',
    status: 'forecast',
    dueDate: '2026-07-12',
    forecastCost: 300000,
    expectedValue: 0,
    stakeholders: [],
    surveyToken: 'tok_ie_duty',
    createdBy: IAN,
    auditTrail: [],
  },
  {
    id: 'proj_ruby_lactalis',
    reportable: true,
    ddi: true,
    stage: 'realise',
    name: 'Ruby (Lactalis)',
    subPortfolio: 'Ops Fin',
    team: 'Operations',
    tier: 'License to Operate',
    status: 'complete',
    dueDate: '2026-03-31',
    actualGoLive: '2026-03-31',
    deliveredInFull: 'Y',
    forecastCost: 0,
    expectedValue: 0,
    stakeholders: [],
    surveyToken: 'tok_ruby_lactalis',
    createdBy: MIGUEL,
    auditTrail: [],
  },
]


// Invented feedback against the two delivered projects, so the survey and
// satisfaction views have something to render.
const seedResponses: SurveyResponse[] = [
  {
    id: 'resp_1',
    projectId: 'proj_visibly_hq',
    respondentEmail: '',
    techSatisfaction: 5,
    peopleSatisfaction: 5,
    submittedAt: nowMinus(100),
    additionalComments: 'The compliance workflows that used to be chased by hand now run themselves, and the audit trail is far easier to evidence. Rollout was well organised and the team were quick to pick up feedback.',
  },
  {
    id: 'resp_2',
    projectId: 'proj_visibly_hq',
    respondentEmail: '',
    techSatisfaction: 4,
    peopleSatisfaction: 5,
    submittedAt: nowMinus(96),
    additionalComments: 'Big time saving on the monthly compliance checks. A few reporting views could be clearer, but the core process is a clear improvement on what we had before.',
  },
  {
    id: 'resp_3',
    projectId: 'proj_guardian_liverpool',
    respondentEmail: '',
    techSatisfaction: 4,
    peopleSatisfaction: 4,
    submittedAt: nowMinus(240),
    additionalComments: 'Raws and packs checks are consistent across shifts now and the paperwork burden has dropped noticeably. Training landed well with the line teams. Some of the search screens take getting used to.',
  },
]

// October 2026: board on Thu 29 Oct, reports due moved by hand a working day
// early to Mon 19 Oct (the rule gives Tue 20 Oct), and locking on the rule on
// Fri 23 Oct.
const OCTOBER = '2026-10'
const OCTOBER_BOARD = '2026-10-29'
const OCTOBER_DUE = '2026-10-19'
const OCTOBER_LOCK = '2026-10-23'

const WINDOW_NAME = monthLabel(OCTOBER)
const SETTINGS_ENTITY = { id: 'global', name: 'Settings' }
const RULES_ENTITY = { id: 'board-date-rules', name: 'Board date rules' }

interface AuditSeed {
  daysAgo: number
  actor: UserRef
  entityType: AuditEntityType
  entityId: string
  entityName: string
  action: string
  field?: string
  before?: unknown
  after?: unknown
}

const auditRow = (seed: AuditSeed): AuditLogEntry => ({
  id: newId('audit'),
  at: nowMinus(seed.daysAgo),
  actorOid: seed.actor.oid,
  actorName: seed.actor.name,
  actorEmail: seed.actor.email,
  entityType: seed.entityType,
  entityId: seed.entityId,
  entityName: seed.entityName,
  action: seed.action,
  field: seed.field ?? null,
  before: seed.before,
  after: seed.after,
})

// Enough rows, actors and entity types to exercise the filters and more than
// one page, without clicking the app into that state first.
const seedAuditRows: AuditSeed[] = [
  { daysAgo: 0.1, actor: NAYAN, entityType: 'reporting_window', entityId: OCTOBER, entityName: WINDOW_NAME, action: 'update', field: 'dueAt', before: '2026-10-20T16:00:00Z', after: '2026-10-19T16:00:00Z' },
  { daysAgo: 0.2, actor: NAYAN, entityType: 'reporting_window', entityId: OCTOBER, entityName: WINDOW_NAME, action: 'create', field: 'ddiBoardDate', after: OCTOBER_BOARD },
  { daysAgo: 0.6, actor: IAN, entityType: 'project', entityId: 'proj_ie_duty', entityName: 'IE Duty Mgt', action: 'update', field: 'dueDate', before: '2026-06-30', after: '2026-07-12' },
  { daysAgo: 2.1, actor: NAYAN, entityType: 'board_date_rules', entityId: RULES_ENTITY.id, entityName: RULES_ENTITY.name, action: 'update', field: 'lockWorkingDaysBefore', before: 3, after: 4 },
  { daysAgo: 2.8, actor: SWAPNA, entityType: 'project', entityId: 'proj_sampling_service', entityName: 'Sampling Service', action: 'update', field: 'stage', before: 'develop', after: 'scale' },
  { daysAgo: 3.5, actor: SELF, entityType: 'settings', entityId: SETTINGS_ENTITY.id, entityName: SETTINGS_ENTITY.name, action: 'update', field: 'reminderGroupEmail', before: 'it-data-northeurope@danone.com', after: 'it-data-uki@danone.com' },
  { daysAgo: 4.2, actor: MIGUEL, entityType: 'product', entityId: 'prod_guardian', entityName: 'Guardian', action: 'update', field: 'description', before: 'Quality management system.', after: 'Quality management system used across the Operations sites.' },
  { daysAgo: 5.9, actor: NAYAN, entityType: 'reporting_window', entityId: '2026-08', entityName: monthLabel('2026-08'), action: 'update', field: 'extensions.IE Duty Mgt', before: null, after: { closesAt: '2026-09-11T16:00:00Z', reason: 'Waiting on the change freeze to lift' } },
  { daysAgo: 7.3, actor: SELF, entityType: 'board_date_rules', entityId: RULES_ENTITY.id, entityName: RULES_ENTITY.name, action: 'update', field: 'dueWorkingDaysBefore', before: 6, after: 7 },
  { daysAgo: 9.0, actor: IAN, entityType: 'project', entityId: 'proj_ne_one_pl', entityName: 'NE One P&L', action: 'update', field: 'dueDate', before: '2026-08-31', after: '2026-09-15' },
  { daysAgo: 11.6, actor: SELF, entityType: 'settings', entityId: SETTINGS_ENTITY.id, entityName: SETTINGS_ENTITY.name, action: 'update', field: 'onTimeDays', before: 7, after: 14 },
  { daysAgo: 14.2, actor: SWAPNA, entityType: 'product', entityId: 'prod_digital_marketing', entityName: 'Digital Marketing - Websites & Content, Mobile Apps mgmt', action: 'update', field: 'vendor', before: 'Adobe Inc.', after: 'Adobe' },
  { daysAgo: 19.4, actor: NAYAN, entityType: 'project', entityId: 'proj_uki_ccd', entityName: 'UKI CCD Automation', action: 'create', after: 'UKI CCD Automation' },
  { daysAgo: 23.0, actor: IAN, entityType: 'project', entityId: 'proj_docxp', entityName: 'DocXP Replacement', action: 'update', field: 'forecastCost', before: 450000, after: 500000 },
  { daysAgo: 27.5, actor: SELF, entityType: 'settings', entityId: SETTINGS_ENTITY.id, entityName: SETTINGS_ENTITY.name, action: 'update', field: 'teams', before: ['Operations', 'Support Functions'], after: ['Operations', 'Support Functions', 'Commercial'] },
  { daysAgo: 31.2, actor: MIGUEL, entityType: 'project', entityId: 'proj_ruby_lactalis', entityName: 'Ruby (Lactalis)', action: 'update', field: 'status', before: 'forecast', after: 'complete' },
  { daysAgo: 38.7, actor: NAYAN, entityType: 'reporting_window', entityId: '2026-08', entityName: monthLabel('2026-08'), action: 'create', field: 'closesAt', after: '2026-09-07T16:00:00Z' },
  { daysAgo: 44.1, actor: SWAPNA, entityType: 'project', entityId: 'proj_baby_bounceback', entityName: 'Baby Bounceback', action: 'create', after: 'Baby Bounceback' },
  { daysAgo: 52.3, actor: IAN, entityType: 'project', entityId: 'proj_homeward_o2c', entityName: 'Homeward O2C', action: 'update', field: 'ddi', before: false, after: true },
  { daysAgo: 60.0, actor: SELF, entityType: 'product', entityId: 'prod_visibly', entityName: 'Visibly', action: 'update', field: 'ownerRef', before: IAN.name, after: NAYAN.name },
  { daysAgo: 71.4, actor: SELF, entityType: 'settings', entityId: SETTINGS_ENTITY.id, entityName: SETTINGS_ENTITY.name, action: 'update', field: 'valueRealisedTargetHrs', before: 80000, after: 100000 },
  { daysAgo: 88.9, actor: NAYAN, entityType: 'project', entityId: 'proj_visibly_hq', entityName: 'Visibly HQ', action: 'update', field: 'expectedValue', before: 2600, after: 3120 },
  { daysAgo: 102.5, actor: SWAPNA, entityType: 'product', entityId: 'prod_digital_marketing', entityName: 'Digital Marketing - Websites & Content, Mobile Apps mgmt', action: 'create', after: 'Digital Marketing - Websites & Content, Mobile Apps mgmt' },
  { daysAgo: 119.0, actor: MIGUEL, entityType: 'product', entityId: 'prod_guardian', entityName: 'Guardian', action: 'create', after: 'Guardian' },
]

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value))

// ── Mutable state (resets on refresh) ────────────────────────────────────────

const state = {
  settings: { ...seedSettings },
  projects: seedProjects.map(p => ({ ...p })),
  responses: seedResponses.map(r => ({ ...r })),
  products: seedProducts.map(p => ({ ...p })),
  users: clone(seedUsers),
  fieldMappings: [] as FieldMapping[],
  boardDateRules: { id: RULES_ENTITY.id, dueWorkingDaysBefore: 7, lockWorkingDaysBefore: 4, auditTrail: [] } as BoardDateRules,
  roleGroups: {
    id: 'role-group-map',
    mappings: seedRoleGroups,
    breakGlassGroupId: GROUPS.platformAdmins,
    auditTrail: [],
  } as RoleGroupMap,
  auditLog: seedAuditRows.map(auditRow),
}

// Mirrors services/audit.record: every mutation writes a central row as well
// as any embedded trail, so the log grows as the app is clicked through.
function recordAudit(
  entityType: AuditEntityType,
  entityId: string,
  entityName: string,
  action: string,
  field?: string,
  before?: unknown,
  after?: unknown,
) {
  state.auditLog.push({
    id: newId('audit'),
    at: new Date().toISOString(),
    actorOid: SELF.oid,
    actorName: SELF.name,
    actorEmail: SELF.email,
    entityType,
    entityId,
    entityName,
    action,
    field: field ?? null,
    before,
    after,
  })
}

const changed = (before: unknown, after: unknown) => JSON.stringify(before) !== JSON.stringify(after)

/** One `update` row per listed field whose value changed. Returns the embedded
 *  trail entries for the same changes. */
function recordChanges(
  entityType: AuditEntityType,
  entity: { id: string; name: string },
  before: object,
  after: object,
  fields: string[],
): AuditEntry[] {
  const entries: AuditEntry[] = []
  for (const field of fields) {
    const was = (before as Record<string, unknown>)[field]
    const now = (after as Record<string, unknown>)[field]
    if (!changed(was, now)) continue
    recordAudit(entityType, entity.id, entity.name, 'update', field, was, now)
    entries.push({ at: new Date().toISOString(), actor: SELF, field, before: was, after: now })
  }
  return entries
}

// Mirrors the `send` helper's thrown error, so callers read `status` the same
// way in mock mode.
const apiError = (message: string, status: number) =>
  Object.assign(new Error(message), { status, data: { detail: message } })

// Month-end review docs, keyed "YYYY-MM". Same shape as the month-reviews
// container: one doc per month with a projectId → {by, at} map.
const monthReviewStore: Record<string, MonthReview> = {}
const monthReviewDoc = (month: string): MonthReview =>
  monthReviewStore[month] ?? (monthReviewStore[month] = { id: month, reviews: {}, extensions: {} })

// A window for the current month so the editor, the month-end page and the
// settings tab all have something real to show: set by hand with no board
// date, locking at 17:00 on the 7th of next month (the fallback rule, made
// visible).
;(() => {
  const now = new Date()
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  const lock = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 7)).toISOString().slice(0, 10)
  monthReviewDoc(month).reportingWindow = {
    opensAt: defaultWindow(month).opens.toISOString(),
    closesAt: deadlineAt(lock).toISOString(),
    dueAt: null,
    ddiBoardDate: null,
    dueMoved: false,
    lockMoved: false,
    note: 'Board pack goes out on the 9th, so please do not leave it to the last day.',
    setBy: NAYAN,
    setAt: new Date(now.getFullYear(), now.getMonth(), 1, 9, 0).toISOString(),
    announcedAt: new Date(now.getFullYear(), now.getMonth(), 1, 9, 1).toISOString(),
    ddiClosedAt: null,
    ddiClosedBy: null,
  }
})()

// Set after the current month's, so it still carries the board date when
// October is the current month. Saved but not yet announced.
monthReviewDoc(OCTOBER).reportingWindow = {
  opensAt: defaultWindow(OCTOBER).opens.toISOString(),
  closesAt: deadlineAt(OCTOBER_LOCK).toISOString(),
  dueAt: deadlineAt(OCTOBER_DUE).toISOString(),
  ddiBoardDate: OCTOBER_BOARD,
  dueMoved: true,
  lockMoved: false,
  note: null,
  setBy: NAYAN,
  setAt: nowMinus(0.1),
  announcedAt: null,
  ddiClosedAt: null,
  ddiClosedBy: null,
}

// Monthly status reports, keyed "{projectId}_{YYYY-MM}" - the same id the
// project-reports container uses, so mock and real behave identically.
const projectReportStore: Record<string, ProjectReport> = {}

// Seeded status reports so the DDI page has something to draw: two months of
// history across the flagged projects, chosen to exercise every cell - a
// worsening row, a closed row, and one that has not filed yet.
const ddiMonth = (delta: number): string => {
  const d = new Date()
  d.setMonth(d.getMonth() + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function seedDdiReport(
  project: Project,
  month: string,
  overall: RagStatus,
  over: Partial<ProjectReport> = {},
): void {
  const rag = { derived: overall, derivedReason: '' }
  projectReportStore[`${project.id}_${month}`] = {
    id: `${project.id}_${month}`,
    projectId: project.id,
    month,
    overall,
    whyLine: '',
    narrative: '',
    scope: rag, schedule: rag, cost: rag, benefit: rag,
    highlights: [], lowlights: [], supportNeeded: [],
    snapshot: {
      projectName: project.name,
      stage: project.stage,
      goLive: project.actualGoLive ?? project.dueDate,
      capex: 0, opex: 0,
      milestones: [], risks: [],
    },
    state: 'submitted',
    ...over,
  } as ProjectReport
}

;(() => {
  const prev = ddiMonth(-1)
  const curr = ddiMonth(0)
  const byId = (id: string) => seedProjects.find(p => p.id === id) as Project

  // The UK&I active portfolio, transcribed from the reference deck so the
  // slide demos with the content the audience already knows. Prev-month
  // RAGs drive the direction arrows; Baby Bounceback files nothing last
  // month, which is what a first appearance looks like.
  const ddiRows: [string, RagStatus | null, RagStatus, DatePrecision, string, string][] = [
    ['proj_homeward_o2c', 'green', 'amber', 'quarter', 'Pending EU CAPEX Committee approval, with a decision expected in September. Following approval, project mobilisation will commence, with delivery teams being onboarded and project kick-off planned for Q4 2026, ahead of implementation during 2027.', 'Unlock funding'],
    ['proj_docxp', 'green', 'green', 'month', '2026 funding has been secured. Immediate focus is to finalise our delivery partner engagement, confirm scope and execute SoW in August in order to mobilise resources and kick off low level design phase in September.', 'Contract negotiations with Delivery partner and mobilise resources for a September kick-off'],
    ['proj_baby_bounceback', null, 'green', 'quarter', 'Cow&Gate website & DTC. Audience targeting precision. Measurement and attribution\nImprovements on Aptamil to enable precision targeting', ''],
    ['proj_sampling_service', 'red', 'green', 'month', 'The New Sampling Site and service is now LIVE! Our enhanced platform and refreshed Nutricia site successfully went live on Tuesday 21st July and is actively processing HCP orders. Formal external launch has been intentionally deferred whilst hypercare addresses Post-Go-Live defects and user experience improvements. Official HCP launch and traffic-driving communications now planned for Monday 27th July.', 'HCP Launch communications planned for 27th July'],
    ['proj_voice_ai_homecare', 'green', 'green', 'month', 'Natterbox AI Phase 2 has completed multiple cycles of testing and refinement. Targeted additional validation is required to ensure full compliance and consistent performance against expected outcomes.', 'Initiate testing with real patients and leverage their feedback to drive final optimisation'],
    ['proj_gross_to_net', 'green', 'green', 'month', 'Change Design Advisory Board validation is complete with 7 term adjustments, 1 new term and 3 granular Non-Conditional terms added in alignment with UKI Fusion Taxonomy. The build is progressing, and testing/cutover planning is underway.', 'Finalise Build and SoW to be received from UpClear based on Design finalisation\nMid to long-term plan to move away from non-approved payment methods to be defined'],
    ['proj_ne_one_pl', 'amber', 'amber', 'month', 'Master Data inconsistencies in the global product hierarchy, presenting challenges to go live. The proposal is to remove global product hierarchy and to focus on Category, Brands and Channels. Global product hierarchy to be re-assessed in the review of the future model, to enable proposed go live date of Sep 15th.', 'Decision on Global Product hierarchy removal achieved\nGo Live'],
    ['proj_finance_backbone', 'green', 'green', 'month', 'A holistic TPM solution review is recommended to define a target-state solution across FMCG and Healthcare. UpClear has provided an initial timeline and cost proposal; however, no major uplifts beyond replicating in BluePlanner capability today in place. Inputs required from Vistex and Salesforce to develop equivalent estimates.', 'Agree on directional assessment approach for TPM\nFollow-on sessions with Salesforce and Vistex to be scheduled and completed'],
    ['proj_uki_ccd', 'green', 'green', 'tbc', 'Process Walkthrough workshops completed, establishing the process baseline and identifying 75 pain points with 11 key themes. Milestone 2 is in progress, with approvals from IT Architecture and Responsible AI obtained and the auto-matching agent designed and ready for prototyping.', 'Develop & test auto-matching agent\nFinalise prioritised improvements roadmap and pilot plan'],
    ['proj_drs', 'green', 'green', 'month', 'DRS discovery continues across invoicing, EDI and SAP requirements, with recent EFC industry workshops providing greater clarity on requirements, integration standards and industry alignment.', 'Baseline scope and requirements, align to emerging EFC standards, and finalise the delivery approach ahead of detailed design'],
    ['proj_ie_duty', 'green', 'amber', 'month', 'The change has been prepared and tested, but some system issues were identified during the final deployment stage. To ensure a smooth and low-risk implementation, the release has been moved to the next available deployment window on 14 September, after the August change freeze.', 'Confirm and fix the issues, retest and redeploy at next available window (currently 14th of Sept)'],
    ['proj_ruby_lactalis', 'green', 'green', 'month', 'TSA exit completed, and moving to closure.', ''],
  ]
  // Sample capex/opex splits in euros for the budget year, so the slide's
  // split bars have figures to draw. Absent means not yet priced (TBC).
  const sampleBudget: Record<string, [number, number]> = {
    proj_gross_to_net: [350_000, 150_000], proj_docxp: [400_000, 100_000], proj_homeward_o2c: [1_500_000, 300_000],
    proj_finance_backbone: [250_000, 150_000], proj_ne_one_pl: [70_000, 30_000],
    proj_sampling_service: [800_000, 300_000], proj_voice_ai_homecare: [60_000, 40_000],
    proj_ie_duty: [200_000, 100_000], proj_drs: [300_000, 150_000],
  }
  for (const [id, prevRag, currRag, precision, ddiSummary, ddiNextSteps] of ddiRows) {
    const project = byId(id)
    // Last month's slide carries the reference texts. This month's status
    // reports carry the same content as their headline and support needed,
    // with the DDI texts left empty, so "Draft slide with AI" has something to
    // draft from - the flow once the month's reports are in.
    if (prevRag) seedDdiReport(project, prev, prevRag, { datePrecision: precision, ddiSummary, ddiNextSteps })
    seedDdiReport(project, curr, currRag, {
      datePrecision: precision,
      overallPrevious: prevRag ?? undefined,
      whyLine: ddiSummary,
      supportNeeded: ddiNextSteps.split('\n').filter(Boolean).map((text, i) => ({ id: `${id}_sn${i}`, text })),
    })
    const budget = sampleBudget[id]
    for (const m of [prev, curr]) {
      const filed = projectReportStore[`${id}_${m}`]
      if (filed && budget) [filed.snapshot.capex, filed.snapshot.opex] = budget
    }
  }
  // One late filer, so the month-end review's "after cut-off" flag has
  // something to show.
  projectReportStore[`proj_ie_duty_${curr}`].submittedAfterDue = true

  // Last month had a due date and a spread of submission times, so the
  // reporting discipline panel has on-time, late and not-filed rows to show.
  // Its DDI texts are closed, as a finished month's would be.
  monthReviewDoc(prev).reportingWindow = {
    opensAt: defaultWindow(prev).opens.toISOString(),
    closesAt: deadlineAt(`${prev}-23`).toISOString(),
    dueAt: deadlineAt(`${prev}-19`).toISOString(),
    ddiBoardDate: `${prev}-28`,
    dueMoved: false,
    lockMoved: false,
    note: null,
    setBy: NAYAN,
    setAt: deadlineAt(`${prev}-01`).toISOString(),
    announcedAt: deadlineAt(`${prev}-01`).toISOString(),
    ddiClosedAt: deadlineAt(`${prev}-24`).toISOString(),
    ddiClosedBy: NAYAN,
  }
  const filedOn = (day: number, hour = 17) =>
    new Date(deadlineAt(`${prev}-${String(day).padStart(2, '0')}`).getTime() - (17 - hour) * 3600_000).toISOString()
  const prevSubmissions: Record<string, [number, number]> = {
    proj_homeward_o2c: [23, 11],   // 4 days late
    proj_docxp: [21, 9],           // 2 days late
    proj_ie_duty: [22, 16],        // 3 days late
    proj_sampling_service: [20, 10], // after the cut-off, but within the grace
    proj_voice_ai_homecare: [17, 14],
    proj_gross_to_net: [18, 12],
    proj_ne_one_pl: [19, 15],
    proj_finance_backbone: [16, 10],
    proj_uki_ccd: [18, 9],
    proj_drs: [19, 16],
    proj_ruby_lactalis: [15, 11],
  }
  for (const [id, [day, hour]] of Object.entries(prevSubmissions)) {
    const filed = projectReportStore[`${id}_${prev}`]
    if (!filed) continue
    filed.submittedAt = filedOn(day, hour)
    filed.submittedBy = NAYAN
    filed.submittedAfterDue = day > 19
  }
  // This month's reports all went in a few days ago, ahead of any due date.
  for (const [id] of ddiRows) {
    const filed = projectReportStore[`${id}_${curr}`]
    if (filed && !filed.submittedAt) { filed.submittedAt = nowMinus(3); filed.submittedBy = NAYAN }
  }
})()

const delay = <T,>(value: T, ms = 120): Promise<T> =>
  new Promise(resolve => setTimeout(() => resolve(value), ms))

// Append an audit entry mimicking the backend diff-and-write behaviour.
const AUDIT_DENYLIST = new Set([
  'lastSyncedAt',
  'surveySentAt',
  'surveyEmailsSent',
  'surveyToken',
  'auditTrail',
  'lastMeaningfulUpdate',
  'monthlyLedger',
])

function applyUpdate(project: Project, updates: Partial<Project>): Project {
  const next = { ...project, ...updates }
  const trail = [...(project.auditTrail || [])]
  for (const [field, after] of Object.entries(updates)) {
    if (AUDIT_DENYLIST.has(field)) continue
    const before = (project as any)[field]
    if (changed(before, after)) {
      trail.push({
        at: new Date().toISOString(),
        actor: SELF,
        field,
        before,
        after,
      })
      recordAudit('project', project.id, project.name, 'update', field, before, after)
    }
  }
  next.auditTrail = trail.slice(-200)
  next.lastMeaningfulUpdate = new Date().toISOString()
  return next
}

// A move to submitted is a new submission only when the report has not been
// submitted already: un-validating a reviewed report keeps its original stamps
// and late flag. Same rule as the backend's project_reports.
const isNewSubmission = (prev: ProjectReport | undefined, nextState: ProjectReport['state']): boolean =>
  nextState === 'submitted' && ((prev?.state ?? 'draft') === 'draft' || !prev?.submittedAt)

// Server-owned, like the sign-off stamps: set on a new submission, cleared
// on the move back to draft, carried otherwise. Mirrors the backend flagging a
// submission made after the month's due time.
function afterDueFlag(month: string, prev: ProjectReport | undefined, nextState: ProjectReport['state']): boolean {
  if (nextState === 'draft') return false
  if (!isNewSubmission(prev, nextState)) return !!prev?.submittedAfterDue
  const dueAt = monthReviewStore[month]?.reportingWindow?.dueAt
  return !!dueAt && Date.now() > new Date(dueAt).getTime()
}

// The DDI texts close and reopen routes, with the backend's refusal.
function setDdiClosed(month: string, close: boolean): Promise<MonthReview> {
  const doc = monthReviewStore[month]
  const window = doc?.reportingWindow
  if (!window) return Promise.reject(apiError('No reporting window has been set for this month.', 404))
  if (close !== !!window.ddiClosedAt) {
    const before = window.ddiClosedAt ?? null
    const closedAt = close ? new Date().toISOString() : null
    doc.reportingWindow = { ...window, ddiClosedAt: closedAt, ddiClosedBy: close ? SELF : null }
    recordAudit('reporting_window', month, monthLabel(month), close ? 'ddi_closed' : 'ddi_reopened', 'ddiClosedAt', before, closedAt)
  }
  return delay({ ...doc, reviews: { ...doc.reviews } })
}

// ── Mock API implementation ──────────────────────────────────────────────────

/** The signed-in person's roles, the way the backend derives them from their
 *  group grants: everyone is a User, and a platform admin runs status
 *  reporting and reads survey results too. Used by getMe and by the routes
 *  that gate on a role. */
const myRoles = (): typeof APP_ROLES[number][] => {
  const grants = state.users.find(u => u.oid === SELF.oid)?.groupGrants ?? []
  const has = (role: string) => grants.some(g => g.role === role)
  return APP_ROLES.filter(role =>
    role === 'User' || has(role) || (role !== 'PlatformAdmin' && has('PlatformAdmin'))
    || (role === 'SurveyResults' && has('SurveyAdmin')))
}

export function createMockApi(): Api {
  console.info('[mockApi] Using in-memory mock API. Data resets on refresh.')

  return {
    // Surveys sent by hand: their own module, see mockSurveys.ts.
    ...createMockSurveyApi(),

    // Projects
    getProjects: () => delay([...state.projects]) as any,
    getProject: (id: string) => delay(state.projects.find(p => p.id === id) || null) as any,
    createProject: (project: unknown) => {
      const p = project as Partial<Project>
      const now = new Date().toISOString()
      const created: Project = {
        id: newId('proj'),
        name: p.name || 'Untitled',
        subPortfolio: p.subPortfolio || state.settings.subPortfolios[0],
        tier: p.tier || 'Winning',
        status: p.status || 'forecast',
        dueDate: p.dueDate || isoDate(0),
        forecastCost: p.forecastCost ?? 0,
        expectedValue: p.expectedValue ?? 0,
        stakeholders: p.stakeholders || [],
        surveyToken: newId('tok'),
        createdBy: SELF,
        auditTrail: [],
        lastMeaningfulUpdate: now,
        ...p,
      } as Project
      state.projects.push(created)
      recordAudit('project', created.id, created.name, 'create', undefined, null, created.name)
      return delay(created) as any
    },
    updateProject: (id: string, updates: unknown) => {
      const idx = state.projects.findIndex(p => p.id === id)
      if (idx < 0) return delay(null) as any
      const updated = updates as Partial<Project>
      // Mimic backend rule: status='complete' requires actualGoLive
      const merged = { ...state.projects[idx], ...updated }
      if (merged.status === 'complete' && !merged.actualGoLive) {
        return Promise.reject(new Error("Cannot set status to 'complete' without an actualGoLive date."))
      }
      const prev = state.projects[idx]
      state.projects[idx] = applyUpdate(prev, updated)
      // Auto-seed ledger on complete transition or complete with empty ledger
      const p = state.projects[idx]
      const wasComplete = prev.status === 'complete'
      const isComplete = p.status === 'complete'
      const hasLedger = Object.keys(p.monthlyLedger || {}).length > 0
      if (isComplete && (!wasComplete || !hasLedger) && p.actualGoLive && (p.expectedValue ?? 0) > 0) {
        p.monthlyLedger = buildSeedLedgerFromDate(p.expectedValue!, new Date(p.actualGoLive))
      }
      return delay(state.projects[idx]) as any
    },
    deleteProject: (id: string) => {
      const deleted = state.projects.find(p => p.id === id)
      state.projects = state.projects.filter(p => p.id !== id)
      if (deleted) recordAudit('project', id, deleted.name, 'delete', undefined, deleted.name, null)
      return delay(null) as any
    },

    getSurveyResponses: (projectId?: string) => {
      const list = projectId ? state.responses.filter(r => r.projectId === projectId) : state.responses
      return delay([...list]) as any
    },
    getSurveyLookup: (projectToken: string, recipientToken: string) => {
      // Mock: match any project by surveyToken, ignore recipient validation.
      const project = state.projects.find(p => p.surveyToken === projectToken)
      if (!project) return delay(null) as any
      return delay({
        projectId: project.id,
        projectName: project.name,
        contact: project.createdBy
          ? { name: project.createdBy.name, email: project.createdBy.email }
          : null,
      }) as any
    },
    checkSurveySubmitted: (_projectToken: string, _recipientToken: string) =>
      delay({ submitted: false }) as any,
    submitSurvey: (body: {
      projectToken: string
      recipientToken: string
      techSatisfaction: number
      peopleSatisfaction: number
      adoption?: number
      valueRealised?: number
      additionalComments?: string
    }) => {
      const project = state.projects.find(p => p.surveyToken === body.projectToken)
      if (!project) return Promise.reject(new Error('Survey not found'))
      state.responses.push({
        id: newId('resp'),
        projectId: project.id,
        respondentEmail: 'anonymous',
        techSatisfaction: body.techSatisfaction,
        peopleSatisfaction: body.peopleSatisfaction,
        adoption: body.adoption,
        valueRealised: body.valueRealised,
        additionalComments: body.additionalComments,
        submittedAt: new Date().toISOString(),
      })
      return delay({ ok: true }) as any
    },

    getFieldMappings: () => delay([...state.fieldMappings]) as any,
    saveFieldMapping: (mapping: unknown) => {
      const m = { ...(mapping as FieldMapping), id: newId('fm') }
      state.fieldMappings.push(m)
      return delay(m) as any
    },
    updateFieldMapping: (id: string, updates: unknown) => {
      const idx = state.fieldMappings.findIndex(m => m.id === id)
      if (idx >= 0) state.fieldMappings[idx] = { ...state.fieldMappings[idx], ...(updates as FieldMapping) }
      return delay(state.fieldMappings[idx] || null) as any
    },

    getSettings: () => delay({ ...state.settings }) as any,
    updateSettings: (settings: unknown) => {
      const before = state.settings
      state.settings = { ...state.settings, ...(settings as Settings) }
      recordChanges('settings', SETTINGS_ENTITY, before, state.settings, Object.keys(settings as Settings))
      return delay(state.settings) as any
    },

    sendSurvey: () => delay({ ok: true }) as any,

    // Products
    getProducts: () => delay([...state.products]) as any,
    getProduct: (id: string) => delay(state.products.find(p => p.id === id) || null) as any,
    createProduct: (product: unknown) => {
      const p = product as Partial<Product>
      const created: Product = {
        id: newId('prod'),
        name: p.name || 'Untitled',
        vendor: p.vendor,
        description: p.description,
        ownerRef: p.ownerRef || SELF,
        contributors: p.contributors || [],
        createdBy: SELF,
        createdAt: new Date().toISOString(),
      }
      state.products.push(created)
      recordAudit('product', created.id, created.name, 'create', undefined, null, created.name)
      return delay(created) as any
    },
    updateProduct: (id: string, updates: unknown) => {
      const idx = state.products.findIndex(p => p.id === id)
      if (idx >= 0) {
        const before = state.products[idx]
        state.products[idx] = { ...before, ...(updates as Product) }
        recordChanges('product', before, before, state.products[idx], Object.keys(updates as Product))
      }
      return delay(state.products[idx] || null) as any
    },
    deleteProduct: (id: string) => {
      const deleted = state.products.find(p => p.id === id)
      state.products = state.products.filter(p => p.id !== id)
      if (deleted) recordAudit('product', id, deleted.name, 'delete', undefined, deleted.name, null)
      return delay(null) as any
    },
    refreshForecastYears: (id: string) => {
      // Mirror the backend logic: pick the newest prior year with any base
      // data, average (base + adjustments) over ACTIVE months only (months
      // with base > 0), then rewrite base on every month of every year
      // from source_year+1 through current_year to that rate. Adjustments
      // on forecast months are preserved.
      const idx = state.projects.findIndex(p => p.id === id)
      if (idx < 0) return delay(null) as any
      const project = state.projects[idx]
      const ledger: Record<string, { base: number; adjustments: number; adjustmentNotes: string[] }> = {
        ...(project.monthlyLedger || {}),
      }
      const curYear = new Date().getFullYear()
      const yearsWithData = new Set<number>()
      for (const key of Object.keys(ledger)) {
        const y = Number(key.slice(0, 4))
        if (!Number.isNaN(y)) yearsWithData.add(y)
      }
      const priorYears = [...yearsWithData].filter(y => y < curYear).sort()
      if (priorYears.length === 0) return delay(project) as any
      const sourceYear = priorYears[priorYears.length - 1]
      const activeTotals: number[] = []
      for (let m = 1; m <= 12; m++) {
        const entry = ledger[`${sourceYear}-${String(m).padStart(2, '0')}`]
        if (!entry || (entry.base || 0) <= 0) continue
        activeTotals.push((entry.base || 0) + (entry.adjustments || 0))
      }
      if (activeTotals.length === 0) return delay(project) as any
      const monthlyRate = activeTotals.reduce((s, v) => s + v, 0) / activeTotals.length
      if (monthlyRate <= 0) return delay(project) as any
      for (let year = sourceYear + 1; year <= curYear; year++) {
        for (let m = 1; m <= 12; m++) {
          const key = `${year}-${String(m).padStart(2, '0')}`
          const existing = ledger[key] || { base: 0, adjustments: 0, adjustmentNotes: [] }
          ledger[key] = { ...existing, base: monthlyRate }
        }
      }
      state.projects[idx] = { ...project, monthlyLedger: ledger }
      return delay(state.projects[idx]) as any
    },

    // What the signed-in tester's groups grant. Everyone signed in holds `User`.
    getMe: () => {
      const fromGroups = state.users.find(u => u.oid === SELF.oid)?.groupGrants ?? []
      const grants = fromGroups.map(({ role, scopes }) => ({ role, scopes: [...scopes] }))
      const me: MeResponse = { ...SELF, roles: myRoles(), grants }
      return delay(me)
    },


    // Users - sorted alphabetically by name to mirror the backend list query.
    // The assignment only comes back when asked for, as on the backend.
    getUsers: (opts?: { includeRoles?: boolean }) => {
      const sorted = clone(state.users).sort((a, b) =>
        (a.name || '').localeCompare(b.name || ''),
      )
      if (opts?.includeRoles) return delay(sorted) as any
      return delay(sorted.map(({ auditTrail: _trail, groupGrants: _group, groupRolesAt: _at, ...user }) => user)) as any
    },
    // Newest first, filtered then paginated - the same order the container
    // query returns.
    getAuditLog: (params?: AuditLogQuery) => {
      const since = params?.since
      const filtered = state.auditLog
        .filter(entry => !params?.entityType || entry.entityType === params.entityType)
        .filter(entry => !params?.actorOid || entry.actorOid === params.actorOid)
        .filter(entry => !since || entry.at >= since)
        .sort((a, b) => b.at.localeCompare(a.at))
      const offset = params?.offset ?? 0
      const limit = params?.limit ?? 100
      const page: AuditLogPage = { entries: clone(filtered.slice(offset, offset + limit)), total: filtered.length }
      return delay(page)
    },

    getAsanaSyncRuns: () => delay([]) as any,
    getNextAsanaSyncRun: () => delay({ nextRunAt: null }) as any,
    getNextReminderSends: () => delay({ ownerSendDate: null, groupSendDate: null }) as any,

    getBauSnapshot: () => delay(null) as any,
    triggerBauSync: () => delay({ message: 'BAU sync triggered' }) as any,

    getJiraSnapshot: () => delay(null) as any,
    triggerJiraSync: () => delay({ message: 'Jira sync triggered' }) as any,

    sendTestReminder: () => delay({ sent: [SELF.email], failed: [], personalised: true, ownerlessProjects: 0 }) as any,
    runOwnerReminders: () => delay({ phase: 'owners', sent: [SELF.email], failed: [], ownerlessProjects: 0 }) as any,
    runGroupReminder: () => delay({ phase: 'group', sent: ['it-data-northeurope@danone.com'], failed: [] }) as any,

    // End-of-month reviews - in-memory docs, validator is the mock signed-in user
    getMonthReview: (month: string) => delay({ ...monthReviewDoc(month), reviews: { ...monthReviewDoc(month).reviews } }) as any,
    setMonthReview: (month: string, projectId: string) => {
      monthReviewDoc(month).reviews[projectId] = { by: SELF, at: new Date().toISOString() }
      return delay(monthReviewDoc(month)) as any
    },
    // The window opens at midnight UK time on the 1st; due and lock are 17:00
    // UK time on their dates. The whole window is written, so anything a
    // stored window carried from before is dropped.
    setReportingWindow: (month: string, body: ReportingWindowUpdate) => {
      const problem = windowProblem(month, { dueDate: body.dueDate ?? '', lockDate: body.lockDate ?? '' })
      if (problem) return Promise.reject(apiError(problem, 400))
      const doc = monthReviewDoc(month)
      const previous = doc.reportingWindow
      const existed = !!previous
      const canSend = !!state.settings.powerAutomateUrl && !!state.settings.reminderGroupEmail
      const opensAt = defaultWindow(month).opens.toISOString()
      const closesAt = deadlineAt(body.lockDate).toISOString()
      const dueAt = body.dueDate ? deadlineAt(body.dueDate).toISOString() : null
      doc.reportingWindow = {
        opensAt, closesAt, dueAt,
        ddiBoardDate: body.ddiBoardDate || null,
        dueMoved: !!body.dueMoved,
        lockMoved: !!body.lockMoved,
        // A save never reopens DDI texts closed by hand.
        ddiClosedAt: previous?.ddiClosedAt ?? null,
        ddiClosedBy: previous?.ddiClosedBy ?? null,
        note: body.note ?? null,
        setBy: SELF, setAt: new Date().toISOString(), announcedAt: canSend ? new Date().toISOString() : null,
      }
      const entity = { id: month, name: monthLabel(month) }
      const fields = ['opensAt', 'closesAt', 'dueAt', 'ddiBoardDate', 'note']
      if (previous) recordChanges('reporting_window', entity, previous, doc.reportingWindow, fields)
      else recordAudit('reporting_window', entity.id, entity.name, 'create', undefined, null, { opensAt, closesAt, dueAt })
      const result: WindowSaveResult = {
        review: doc, emailSent: canSend,
        emailDetail: canSend
          ? `Announcement ${existed ? '(updated) ' : ''}sent to ${state.settings.reminderGroupEmail}`
          : 'No group email address is configured in Settings',
      }
      return delay(result) as any
    },
    setReportingExtension: (month: string, projectId: string, body: { closesAt: string; reason: string }) => {
      const doc = monthReviewDoc(month)
      const project = state.projects.find(p => p.id === projectId)
      const to = project?.projectManager?.email || project?.createdBy?.email
      const before = doc.extensions?.[projectId]
      doc.extensions = { ...(doc.extensions ?? {}), [projectId]: {
        closesAt: body.closesAt, reason: body.reason, grantedBy: SELF,
        grantedAt: new Date().toISOString(), notifiedAt: to ? new Date().toISOString() : null,
      } }
      recordAudit(
        'reporting_window', month, monthLabel(month), 'update', `extensions.${project?.name ?? projectId}`,
        before ? { closesAt: before.closesAt, reason: before.reason } : null, body,
      )
      const result: WindowSaveResult = {
        review: doc, emailSent: !!to,
        emailDetail: to ? `Extension notice sent to ${to}` : 'The project has no project manager or owner email to notify',
      }
      return delay(result) as any
    },
    clearReportingExtension: (month: string, projectId: string) => {
      const doc = monthReviewDoc(month)
      const before = doc.extensions?.[projectId]
      if (doc.extensions) delete doc.extensions[projectId]
      if (before) {
        const project = state.projects.find(p => p.id === projectId)
        recordAudit(
          'reporting_window', month, monthLabel(month), 'update', `extensions.${project?.name ?? projectId}`,
          { closesAt: before.closesAt, reason: before.reason }, null,
        )
      }
      return delay(doc) as any
    },
    // A month's DDI texts, closed and reopened by hand once its window is
    // saved. Closing twice keeps the first who and when; neither writes when
    // nothing changes.
    closeDdiTexts: (month: string) => setDdiClosed(month, true),
    reopenDdiTexts: (month: string) => setDdiClosed(month, false),
    // One row's draft, the same stub as the slide draft uses.
    redraftProjectReportDdi: async (month: string, projectId: string) => {
      const r = projectReportStore[`${projectId}_${month}`]
      if (!r) throw apiError('No report has been filed for this project and period.', 404)
      if (monthReviewDoc(month).reportingWindow?.ddiClosedAt) throw apiError('The DDI texts for this month are closed.', 403)
      await delay(null, 1200)
      const actions = r.supportNeeded.map(b => b.text).filter(Boolean)
      const open = r.snapshot.milestones.filter(m => m.status === 'planned' || m.status === 'at_risk').map(m => m.label)
      const ddiSummary = (r.whyLine || r.narrative.split('\n')[0] || r.ddiSummary || '').trim()
      const ddiNextSteps = (actions.length ? actions : open).slice(0, 2).join('\n') || r.ddiNextSteps || ''
      if (!ddiSummary && !ddiNextSteps) throw apiError("There is not enough in this month's report to draft from yet.", 502)
      // The backend runs the same check on a redraft as on the whole slide, so
      // the mock flags one too: a number the report does not contain.
      const issues = /\d/.test(ddiSummary) ? ['Not found in the report: 27 (sample flag in mock mode)'] : []
      return { ddiSummary, ddiNextSteps, issues }
    },
    // Same rules and progress steps as the backend's ddi_drafts.draft_slide,
    // with its no-model stub: the headline as the summary, support needed (or
    // the open milestones) as the way forward. Paced so the pipeline shows.
    draftDdiSlide: async (month: string, onProgress: (e: DdiDraftEvent) => void, signal?: AbortSignal): Promise<DdiDraftResult> => {
      // A stop lands between rows, as it does on the server.
      const stopped = () => { if (signal?.aborted) throw new DOMException('Drafting was stopped.', 'AbortError') }
      const review = monthReviewDoc(month)
      // The one billed run a month, so the backend keeps it to reporting admins.
      if (!myRoles().includes('StatusReportingAdmin')) throw apiError('Status reporting admin role required', 403)
      if (review.ddiDraftedAt) throw apiError('The DDI slide for this month has already been drafted.', 409)
      const ddiIds = new Set(state.projects.filter(p => p.ddi).map(p => p.id))
      const filed = Object.values(projectReportStore).filter(r => r.month === month && ddiIds.has(r.projectId))
      if (!filed.length) throw apiError('No status reports have been filed for the DDI projects this month yet.', 400)
      const todo = filed.filter(r => !r.ddiSummary?.trim() && !r.ddiNextSteps?.trim())
      const prevMonth = shiftMonth(month, -1)
      const withPrev = filed.filter(r => projectReportStore[`${r.projectId}_${prevMonth}`]).length
      await delay(null, 700)
      onProgress({ stage: 'gather', status: 'done', detail: `${filed.length} of ${ddiIds.size} status reports filed, ${withPrev} with last month to compare, ${todo.length} rows to draft` })

      const failed: string[] = []
      const drafts: { r: ProjectReport; summary: string; nextSteps: string }[] = []
      onProgress({ stage: 'draft', status: 'running', done: 0, total: todo.length })
      for (const [i, r] of todo.entries()) {
        await delay(null, 260)
        stopped()
        const actions = r.supportNeeded.map(b => b.text).filter(Boolean)
        const open = r.snapshot.milestones.filter(m => m.status === 'planned' || m.status === 'at_risk').map(m => m.label)
        const summary = (r.whyLine || r.narrative.split('\n')[0] || '').trim()
        const nextSteps = (actions.length ? actions : open).slice(0, 2).join('\n')
        if (!summary && !nextSteps) failed.push(r.snapshot.projectName)
        else drafts.push({ r, summary, nextSteps })
        onProgress({ stage: 'draft', status: 'running', done: i + 1, total: todo.length, row: r.snapshot.projectName })
      }
      onProgress({ stage: 'draft', status: 'done', detail: `${drafts.length} drafted${failed.length ? `, ${failed.length} not enough to draft from` : ''}` })
      if (todo.length && !drafts.length) throw apiError('The AI could not draft any rows. Try again in a moment.', 502)

      // The stub copies the report's own words, so nothing is invented; one
      // row is flagged anyway so the demo shows what a flag looks like.
      await delay(null, 700)
      const flagged = drafts.slice(0, 1).map(d => ({ row: d.r.snapshot.projectName, issues: ['Not found in the report: 27 (sample flag in mock mode)'] }))
      onProgress({ stage: 'check', status: 'done', detail: flagged.length ? `${flagged.length} row flagged to check closely` : 'Every number and date traced back to the reports' })

      for (const [i, d] of drafts.entries()) {
        await delay(null, 60)
        stopped()
        const issues = flagged.find(f => f.row === d.r.snapshot.projectName)?.issues ?? []
        projectReportStore[d.r.id] = {
          ...d.r, ddiSummary: d.summary, ddiNextSteps: d.nextSteps, ddiAiIssues: issues,
          ddiUpdatedBy: SELF, ddiUpdatedAt: new Date().toISOString(),
        }
        recordAudit('project', d.r.projectId, d.r.snapshot.projectName, 'ddi_drafted', `${month} summary`, d.r.ddiSummary ?? '', d.summary)
        if (d.nextSteps) recordAudit('project', d.r.projectId, d.r.snapshot.projectName, 'ddi_drafted', `${month} way forward`, d.r.ddiNextSteps ?? '', d.nextSteps)
        onProgress({ stage: 'save', status: 'running', done: i + 1, total: drafts.length })
      }
      review.ddiDraftedAt = new Date().toISOString()
      review.ddiDraftedBy = SELF
      onProgress({ stage: 'save', status: 'done', detail: `${drafts.length} rows on the slide as AI drafts` })
      return { review: { ...review }, drafted: drafts.length, failed: failed.sort(), flagged }
    },
    getRoleGroups: () => delay(clone(state.roleGroups)),
    setRoleGroups: (mappings: RoleGroupMapping[]) => {
      const problem = roleGroupsProblem(mappings)
      if (problem) return Promise.reject(apiError(problem, 400))
      const before = state.roleGroups.mappings
      const after = [...mappings]
        .map(m => ({ ...m, scopes: [...new Set(m.scopes.map(s => s.trim()).filter(Boolean))].sort() }))
        .sort((a, b) => a.role.localeCompare(b.role) || a.groupName.localeCompare(b.groupName))
      if (changed(before, after)) {
        state.roleGroups = {
          ...state.roleGroups,
          mappings: after,
          auditTrail: [
            ...state.roleGroups.auditTrail,
            { at: new Date().toISOString(), actor: SELF, field: 'mappings', before, after },
          ].slice(-200),
        }
        recordAudit('role_group_map', 'role-group-map', 'Role groups', 'update', 'mappings', before, after)
      }
      return delay(clone(state.roleGroups))
    },
    getBoardDateRules: () => delay(clone(state.boardDateRules)),
    setBoardDateRules: (body: BoardDateRulesUpdate) => {
      const problem = rulesProblem(body)
      if (problem) return Promise.reject(apiError(problem, 400))
      const before = state.boardDateRules
      const next = { dueWorkingDaysBefore: body.dueWorkingDaysBefore, lockWorkingDaysBefore: body.lockWorkingDaysBefore }
      const entries = recordChanges('board_date_rules', RULES_ENTITY, before, next, ['dueWorkingDaysBefore', 'lockWorkingDaysBefore'])
      if (entries.length) {
        state.boardDateRules = { ...before, ...next, auditTrail: [...before.auditTrail, ...entries].slice(-200) }
      }
      return delay(clone(state.boardDateRules))
    },
    clearMonthReview: (month: string, projectId: string) => {
      delete monthReviewDoc(month).reviews[projectId]
      return delay(monthReviewDoc(month)) as any
    },

    // Monthly status reports - in-memory, keyed exactly like the real container.
    // getProjectReport resolves null when nothing is filed, matching the API:
    // "not written yet" is the normal starting state, not an error.
    getProjectReports: (month: string) =>
      delay(Object.values(projectReportStore).filter(r => r.month === month)) as any,
    getProjectReport: (month: string, projectId: string) =>
      delay(projectReportStore[`${projectId}_${month}`] ?? null) as any,
    saveProjectReport: (month: string, projectId: string, report: unknown) => {
      const id = `${projectId}_${month}`
      const prev = projectReportStore[id]
      // Mirrors the backend: content only. State, its sign-off stamps and the
      // after-cut-off flag stay as stored, a new report starts as a draft, and
      // only setProjectReportState moves them.
      const next: ProjectReport = {
        ...(report as ProjectReport),
        id, projectId, month,
        state: prev?.state ?? 'draft',
        submittedBy: prev?.submittedBy,
        submittedAt: prev?.submittedAt,
        reviewedBy: prev?.reviewedBy,
        reviewedAt: prev?.reviewedAt,
        submittedAfterDue: !!prev?.submittedAfterDue,
        updatedBy: SELF,
        updatedAt: new Date().toISOString(),
      }
      // DDI texts belong to the DDI page; the editor's save must not carry a
      // stale copy over them. Same rule as the backend's upsert_report.
      next.ddiSummary = prev?.ddiSummary
      next.ddiNextSteps = prev?.ddiNextSteps
      next.ddiAiIssues = prev?.ddiAiIssues ?? []
      projectReportStore[id] = next
      return delay(next) as any
    },
    setProjectReportState: (month: string, projectId: string, next: string, expectedState?: string) => {
      const id = `${projectId}_${month}`
      const doc = projectReportStore[id]
      if (!doc) return Promise.reject(new Error('No report has been filed for this project and period.'))
      // Same compare-and-set as the backend: a report that has left the
      // expected state comes back unchanged.
      if (doc.state === next || (expectedState && doc.state !== expectedState)) return delay(doc) as any
      const now = new Date().toISOString()
      const updated = {
        ...doc,
        state: next as ProjectReport['state'],
        submittedAfterDue: afterDueFlag(month, doc, next as ProjectReport['state']),
        updatedBy: SELF,
        updatedAt: now,
      }
      if (isNewSubmission(doc, next as ProjectReport['state'])) { updated.submittedBy = SELF; updated.submittedAt = now }
      else if (next === 'reviewed') { updated.reviewedBy = SELF; updated.reviewedAt = now }
      else if (next === 'draft') {
        updated.submittedBy = updated.submittedAt = updated.reviewedBy = updated.reviewedAt = undefined
      }
      projectReportStore[id] = updated
      return delay(updated) as any
    },
    setProjectReportDdi: (
      month: string,
      projectId: string,
      texts: { ddiSummary: string; ddiNextSteps: string },
    ) => {
      const id = `${projectId}_${month}`
      const doc = projectReportStore[id]
      if (!doc) return Promise.reject(new Error('No report has been filed for this project and period.'))
      // Texts closed by hand are closed for everyone, as in assert_ddi_editable.
      if (monthReviewDoc(month).reportingWindow?.ddiClosedAt) {
        return Promise.reject(apiError('The DDI texts for this month are closed. Reopen them to make changes.', 403))
      }
      if (doc.ddiSummary === texts.ddiSummary && doc.ddiNextSteps === texts.ddiNextSteps && !doc.ddiAiIssues?.length) {
        return delay(doc) as any
      }
      // Never touches state or the sign-off stamps - see set_ddi_texts.
      const updated: ProjectReport = {
        ...doc,
        ddiSummary: texts.ddiSummary,
        ddiNextSteps: texts.ddiNextSteps,
        // A person has looked at the row now, so the AI check's flags go.
        ddiAiIssues: [],
        // The slide texts carry their own stamp; `updatedBy` stays the PM's.
        ddiUpdatedBy: SELF,
        ddiUpdatedAt: new Date().toISOString(),
      }
      projectReportStore[id] = updated
      // One audit row per text that changed, as set_ddi_texts writes.
      for (const [field, was, now] of [
        [`${month} summary`, doc.ddiSummary ?? '', texts.ddiSummary],
        [`${month} way forward`, doc.ddiNextSteps ?? '', texts.ddiNextSteps],
      ] as const) {
        if (was !== now) recordAudit('project', doc.projectId, doc.snapshot.projectName, 'ddi_texts', field, was, now)
      }
      return delay(updated) as any
    },
    deleteProjectReport: (month: string, projectId: string) => {
      delete projectReportStore[`${projectId}_${month}`]
      return delay(null) as any
    },

    // Saved views - in-memory stubs
    getViews: (_page: string) => delay([]) as any,
    createView: (body: unknown) => delay({ id: newId('view'), userId: 'mock', ...(body as object), createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }) as any,
    updateView: (_id: string, body: unknown) => delay(body) as any,
    deleteView: (_id: string) => delay(null) as any,
  }
}
